import { BinaryQuestionTemplate } from '@templates';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RoutesList } from '../../../../routes/Routes';
import { ZodError } from 'zod';
import { ErrorMessages } from '@types';
import { contactUserOptionsSchema } from '../../validations/index';
import { handleLoginPageEvent, msalInstance, parseZodErrors } from '@utils';
import {
  useSignsBollardsPayload,
  useSignsBollardsStore,
  useHandleSubmit,
} from '../../../../hooks';
import { useUserData } from '@hooks';

const pageTitle = `Can we contact you about your issue?`;
const description = `By providing your details, you agree that we may contact you by phone or email if more information is needed.`;

export const ContactUserOption = () => {
  const { data, get, update } = useSignsBollardsStore();
  const navigate = useNavigate();
  const [errorMessages, setErrorMessages] = useState<ErrorMessages[]>([]);
  const [idProvider, setIdProvider] = useState<string>('');
  const [crmId, setCrmId] = useState<string>('');
  const { isLoggedInUser } = useUserData(crmId);
  const { mutate: submitForm, isPending } = useHandleSubmit({
    confirmationRoute: RoutesList.CONFIRMATION,
    endpoint: '/report/v1/roads-and-streets/signs-and-bollards',
  });

  const payload = useSignsBollardsPayload();

  useEffect(() => {
    if (!crmId) {
      handleLoginPageEvent(msalInstance, setCrmId, setIdProvider);
    }
  }, [crmId, idProvider]);

  const handleChange = (value: string) => {
    update('contactUserOption', value);
    setErrorMessages([]);
  };

  const contactUserOption = get('contactUserOption');
  const wantConfirmationEmail = get('wantConfirmationEmail');

  const isUserDetailsPage =
    (wantConfirmationEmail.toLowerCase() === 'yes' &&
      contactUserOption.toLowerCase() === 'no') ||
    contactUserOption.toLowerCase() === 'yes';
  const handleNext = () => {
    try {
      contactUserOptionsSchema.parse({
        canWeContactYou: get('contactUserOption'),
      });
      isUserDetailsPage
        ? navigate(RoutesList.YOUR_DETAILS)
        : submitForm({
            payload,
            isLoggedInUser,
          });
    } catch (error) {
      const validationErrors = parseZodErrors(error as ZodError);
      setErrorMessages(
        validationErrors.map((error: ErrorMessages) => ({
          ...error,
          href: error.path,
          message: 'Select if we can contact you about your issue',
        })),
      );
    }
  };

  const buttonNext = {
    label:
      isUserDetailsPage || data?.contactUserOption === '' ? 'Next' : 'Submit',
    onClick: handleNext,
    loading: isPending,
  };

  const buttonPrevious = {
    onClick: () => navigate(RoutesList.EMAIL_CONFIRMATION),
  };
  const options = [
    {
      labelText: 'Yes',
      isDefault: data.contactUserOption === 'Yes',
      isLabelBold: false,
    },
    {
      labelText: 'No',
      isDefault: data.contactUserOption === 'No',
      isLabelBold: false,
    },
  ];

  return (
    <BinaryQuestionTemplate
      title={pageTitle}
      description={description}
      options={options}
      onChange={handleChange}
      buttonNext={buttonNext}
      buttonPrevious={buttonPrevious}
      errorPath="canWeContactYou"
      errorMessages={errorMessages}
    />
  );
};
